import { useCallback, useRef } from "react";

// Scroll-reveal manager — the successor to the per-component observer that
// pull-quote removed. That observer stranded content invisible whenever a
// scroll went unsampled; this module is built so that CANNOT happen:
//
//   1. The hidden state (opacity: 0) only exists under `html.lvs-js`
//      (stamped in src/index.tsx) AND `prefers-reduced-motion: no-preference`
//      — prerendered HTML, no-JS browsers, crawlers and reduced-motion
//      visitors always see full content. See styles/base/_motion.scss.
//   2. Reveals fire from four independent sources: the IntersectionObserver,
//      a mount-time sweep, passive scroll/resize sweeps, and a last-resort
//      timer that reveals everything still pending, without animation.
//
// The reveal itself is pure CSS (`is-in` class → animation), so this file
// only decides WHEN an element shows, never WHETHER.

const FAILSAFE_MS = 8000;

const pending = new Set<HTMLElement>();
let observer: IntersectionObserver | null = null;
let listening = false;
let sweepScheduled = false;
let failsafeTimer: number | undefined;

/** Stamp the js-capability class. Call once, before the app renders. */
export function markMotionReady(): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.add("lvs-js");
}

function getObserver(): IntersectionObserver {
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target as HTMLElement);
      }
    },
    // Start the entrance slightly before the element is fully in frame.
    { rootMargin: "0px 0px -10% 0px", threshold: 0 }
  );
  return observer;
}

function reveal(el: HTMLElement, instant = false): void {
  pending.delete(el);
  observer?.unobserve(el);
  if (instant) el.classList.add("is-instant");
  el.classList.add("is-in");
  if (pending.size === 0) stopListening();
}

function inViewport(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false; // display:none / jsdom
  return r.top < window.innerHeight && r.bottom > 0;
}

function sweep(): void {
  sweepScheduled = false;
  for (const el of Array.from(pending)) {
    if (inViewport(el)) reveal(el);
  }
}

function scheduleSweep(): void {
  if (sweepScheduled) return;
  sweepScheduled = true;
  requestAnimationFrame(sweep);
}

function startListening(): void {
  if (listening) return;
  listening = true;
  window.addEventListener("scroll", scheduleSweep, { passive: true });
  window.addEventListener("resize", scheduleSweep);
}

function stopListening(): void {
  if (!listening) return;
  listening = false;
  window.removeEventListener("scroll", scheduleSweep);
  window.removeEventListener("resize", scheduleSweep);
}

function armFailsafe(): void {
  if (failsafeTimer !== undefined) return;
  failsafeTimer = window.setTimeout(() => {
    failsafeTimer = undefined;
    // A healthy observer has already revealed anything at or near the
    // viewport — whatever is still pending there was missed and shows now,
    // plainly. Elements far below the fold stay queued so their entrance
    // still plays when the visitor actually reaches them; the timer re-arms
    // until the queue drains. Content > choreography, without disabling
    // choreography for slow readers.
    for (const el of Array.from(pending)) {
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue; // hidden; observer's job
      const nearViewport = r.bottom > -window.innerHeight && r.top < window.innerHeight * 2;
      if (nearViewport) reveal(el, true);
    }
    if (pending.size > 0) armFailsafe();
  }, FAILSAFE_MS);
}

function register(el: HTMLElement): void {
  if (el.classList.contains("is-in")) return;
  if (typeof IntersectionObserver === "undefined") {
    reveal(el, true);
    return;
  }
  pending.add(el);
  getObserver().observe(el);
  startListening();
  scheduleSweep();
  armFailsafe();
}

function unregister(el: HTMLElement): void {
  pending.delete(el);
  observer?.unobserve(el);
  if (pending.size === 0) stopListening();
}

/**
 * Attach an element to the reveal manager. Pair with the `lvs-reveal`
 * (single element) or `lvs-reveal-group` (staggered children) classname;
 * an optional inline `--lvs-d` custom property delays a single reveal.
 *
 * Returns a CALLBACK ref on purpose: registration must follow the element's
 * attach/detach, not the component's mount. React Router reuses a page
 * instance across sibling routes (e.g. service → service), so a section that
 * appears only for the next route mounts long after any []-dep effect ran —
 * a mount-scoped effect would leave it registered never, hidden forever.
 */
export function useReveal<T extends HTMLElement = HTMLElement>(): React.RefCallback<T> {
  const prev = useRef<T | null>(null);
  return useCallback((el: T | null) => {
    if (prev.current && prev.current !== el) unregister(prev.current);
    prev.current = el;
    if (el) register(el);
  }, []);
}

// Exposed for tests only.
export const __motion = {
  pending,
  sweep,
  reveal,
  FAILSAFE_MS,
  clearFailsafe(): void {
    if (failsafeTimer !== undefined) {
      window.clearTimeout(failsafeTimer);
      failsafeTimer = undefined;
    }
  },
};
